import React, { useState, useEffect, useCallback, useMemo, useDeferredValue } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { useToast } from '../contexts/ToastContext';
import { useClubSettings } from '../contexts/ClubSettingsContext';
import {
  BanknotesIcon,
  CheckCircleIcon,
  XCircleIcon,
  CogIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PencilIcon,
  ArrowDownTrayIcon,
  BellAlertIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { isAdmin, isParent } from '../types/auth';
import { membershipFeesAPI, exportsAPI } from '../services/api';
import { getErrorMessage } from '../utils/errorMessage';
import {
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/solid';
import type {
  MembershipFee,
  MembershipFeeSettings,
  FeeStatistics,
  OutstandingFees,
} from '../types/membershipFee';
import { MONTH_NAMES } from '../types/membershipFee';
import FeeSettingsPanel from '../components/membershipFees/FeeSettingsPanel';
import FeeStatisticsPanel from '../components/membershipFees/FeeStatisticsPanel';
import ParentFeesView from '../components/membershipFees/ParentFeesView';
import GenerateFeesModal from '../components/membershipFees/GenerateFeesModal';
import RemindFeesModal from '../components/membershipFees/RemindFeesModal';
import { SkeletonStatCards, SkeletonTable } from '../components/ui/Skeleton';
import LazyLoadTrigger from '../components/ui/LazyLoadTrigger';
import { useBatchedList } from '../hooks/useBatchedList';
import { useIsMobile } from '../hooks/useIsMobile';
import { matchesSearch } from '../utils/search';

/** Ennyi sor jelenik meg egyszerre; görgetéskor jön a következő adag. */
const PAGE_SIZE = 60;

const formatAmount = (amount: number) => `${amount.toLocaleString('hu-HU')} Ft`;

const MembershipFees: React.FC = () => {
  const { user } = useAuth();
  usePageTitle('Tagdíjak');
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { settings: clubSettings, loading: clubSettingsLoading } = useClubSettings();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [fees, setFees] = useState<MembershipFee[]>([]);
  const [settings, setSettings] = useState<MembershipFeeSettings | null>(null);
  const [statistics, setStatistics] = useState<FeeStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [remindOpen, setRemindOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'paid' | 'unpaid'>('all');
  const [ageGroupFilter, setAgeGroupFilter] = useState('');
  const [selectedFeeIds, setSelectedFeeIds] = useState<Set<number>>(new Set());
  const [bulkPaying, setBulkPaying] = useState(false);
  const deferredSearch = useDeferredValue(search);
  const isMobile = useIsMobile();
  const [outstanding, setOutstanding] = useState<OutstandingFees | null>(null);
  const [showOutstandingDetails, setShowOutstandingDetails] = useState(false);
  const [editingFeeId, setEditingFeeId] = useState<number | null>(null);
  const [editAmount, setEditAmount] = useState('');
  const [editingOverridePlayerId, setEditingOverridePlayerId] = useState<number | null>(null);
  const [overrideAmount, setOverrideAmount] = useState('');
  const [exporting, setExporting] = useState(false);
  const [remindingId, setRemindingId] = useState<number | 'all' | null>(null);

  /** `silent`: a lista helyben marad, nem vált csontváz-töltőre (mentés utáni frissítéshez). */
  const loadStaffData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const [feesData, statsData, outstandingData] = await Promise.all([
        membershipFeesAPI.getFees(year, month),
        membershipFeesAPI.getStatistics(year, month),
        membershipFeesAPI.getOutstanding(),
      ]);
      setFees(feesData);
      setStatistics(statsData);
      setOutstanding(outstandingData);
      if (isAdmin(user)) {
        const settingsData = await membershipFeesAPI.getSettings();
        setSettings(settingsData);
      }
    } catch {
      setError('Hiba történt az adatok betöltésekor');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [year, month, user]);

  /** A statisztika és a kintlévőség csendes frissítése - a lista sorai már helyben frissültek. */
  const refreshSummary = useCallback(async () => {
    try {
      const [statsData, outstandingData] = await Promise.all([
        membershipFeesAPI.getStatistics(year, month),
        membershipFeesAPI.getOutstanding(),
      ]);
      setStatistics(statsData);
      setOutstanding(outstandingData);
    } catch {
      // A lista jó, csak az összesítő maradt le - a következő betöltés pótolja
    }
  }, [year, month]);

  // Hónapváltáskor a kijelölés nem visz át
  useEffect(() => {
    setSelectedFeeIds(new Set());
  }, [year, month]);

  // A szülői nézet maga tölti be az adatait (ParentFeesView) - itt csak a betöltés jelző áll le
  const loadParentData = useCallback(async () => {
    setLoading(false);
  }, []);

  useEffect(() => {
    // Ha a klub kikapcsolta a tagdíjszedést, az oldal ne is kérdezzen le
    // semmit - a linket sehol nem mutatjuk, de közvetlen URL-lel még elérhető
    // lenne, ezért itt is el kell terelni.
    if (clubSettingsLoading) return;

    if (clubSettings && !clubSettings.membershipFeesEnabled) {
      navigate('/', { replace: true });
      return;
    }

    // A klub tagdíjait csak az admin kezeli; az edző legfeljebb szülőként, a saját gyereke díjait látja
    if (isAdmin(user)) {
      loadStaffData();
    } else if (isParent(user)) {
      loadParentData();
    } else {
      // Tiszta Player (nincs se stáb, se szülő képessége): a tagdíj a szülőt
      // vagy a sajátfiókos felnőttet terheli, neki itt nincs mit mutatni.
      // Enélkül a loading a kezdeti true-n ragadt volna örökre, mert egyik
      // betöltő függvény sem futott volna le.
      setLoading(false);
    }
  }, [user, clubSettings, clubSettingsLoading, navigate, loadStaffData, loadParentData]);

  /** Helyben frissíti a sorokat, hogy a lista ne ugráljon és ne töltődjön újra. */
  const applyPaid = (ids: Set<number>, isPaid: boolean) =>
    setFees((prev) =>
      prev.map((f) =>
        ids.has(f.id) ? { ...f, isPaid, paidAt: isPaid ? f.paidAt ?? new Date().toISOString() : undefined } : f
      )
    );

  const handleMarkPayment = async (feeId: number, isPaid: boolean) => {
    const ids = new Set([feeId]);
    applyPaid(ids, isPaid);
    try {
      await membershipFeesAPI.markPayment(feeId, { isPaid });
      refreshSummary();
    } catch {
      applyPaid(ids, !isPaid);
      showToast('Hiba történt a fizetés jelölésekor', 'error');
    }
  };

  const handleBulkMark = async (isPaid: boolean) => {
    const ids = new Set(selectedFeeIds);
    if (ids.size === 0) return;
    setBulkPaying(true);
    try {
      const result = await membershipFeesAPI.markPayments(Array.from(ids), isPaid);
      applyPaid(ids, isPaid);
      setSelectedFeeIds(new Set());
      showToast(
        isPaid ? `${result.updatedCount} tagdíj fizetettnek jelölve.` : `${result.updatedCount} fizetés visszavonva.`,
        'success'
      );
      refreshSummary();
    } catch (err) {
      showToast(getErrorMessage(err, 'A csoportos jelölés nem sikerült.'), 'error');
    } finally {
      setBulkPaying(false);
    }
  };

  const handleSaveAmount = async (feeId: number) => {
    const parsed = parseFloat(editAmount);
    if (isNaN(parsed) || parsed < 0) return;
    try {
      await membershipFeesAPI.updateFeeAmount(feeId, parsed);
      setEditingFeeId(null);
      await loadStaffData(true);
    } catch {
      setError('Hiba történt az összeg módosításakor');
    }
  };

  const handleSaveOverride = async (playerId: number) => {
    const value = overrideAmount.trim() === '' ? null : parseFloat(overrideAmount);
    if (value !== null && (isNaN(value) || value < 0)) return;
    try {
      await membershipFeesAPI.setPlayerOverride(playerId, { customMonthlyFee: value });
      setEditingOverridePlayerId(null);
      await loadStaffData(true);
    } catch {
      setError('Hiba történt az egyedi díj beállításakor');
    }
  };

  const goToPrevMonth = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  };

  const goToNextMonth = () => {
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
  };

  const ageGroupOptions = useMemo(
    () => Array.from(new Set(fees.map((f) => f.ageGroup).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'hu')),
    [fees]
  );

  const filteredFees = useMemo(
    () =>
      fees.filter(
        (f) =>
          matchesSearch(deferredSearch, f.playerName) &&
          (filterStatus === 'all' || (filterStatus === 'paid' ? f.isPaid : !f.isPaid && f.amount > 0)) &&
          (!ageGroupFilter || f.ageGroup === ageGroupFilter)
      ),
    [fees, deferredSearch, filterStatus, ageGroupFilter]
  );

  const { visible: visibleFees, hasMore: hasMoreFees, loadMore: loadMoreFees } = useBatchedList(filteredFees, PAGE_SIZE);

  const allFilteredSelected = filteredFees.length > 0 && filteredFees.every((f) => selectedFeeIds.has(f.id));
  const toggleFee = (id: number) =>
    setSelectedFeeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAllFiltered = () =>
    setSelectedFeeIds((prev) => {
      const next = new Set(prev);
      filteredFees.forEach((f) => (allFilteredSelected ? next.delete(f.id) : next.add(f.id)));
      return next;
    });

  if (loading) {
    return (
      <div className="py-6">
        <SkeletonStatCards count={4} />
        <SkeletonTable rows={6} cols={5} />
      </div>
    );
  }

  // Szülői nézet - az edző-szülő is ezt kapja: a klub tagdíjai csak az adminra tartoznak.
  // Az admin-szülő a teljes listát látja, abban a gyereke díja is benne van.
  if (isParent(user) && !isAdmin(user)) {
    return (
      <div>
        <div className="flex items-center mb-6">
          <BanknotesIcon className="h-7 w-7 text-primary-600 mr-3" />
          <h1 className="text-2xl font-bold text-gray-900">Tagdíjak</h1>
        </div>

        <div className="max-w-2xl">
          <ParentFeesView />
        </div>
      </div>
    );
  }

  // Tiszta Player, vagy edző gyerek nélkül: a klub tagdíjai az adminra tartoznak
  if (!isAdmin(user)) {
    return (
      <div>
        <div className="flex items-center mb-6">
          <BanknotesIcon className="h-7 w-7 text-primary-600 mr-3" />
          <h1 className="text-2xl font-bold text-gray-900">Tagdíjak</h1>
        </div>
        <p className="text-sm text-gray-500">Ez az oldal rád nem vonatkozik.</p>
      </div>
    );
  }

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportsAPI.fees(year);
    } catch (err) {
      showToast(getErrorMessage(err, 'Az exportálás nem sikerült.'), 'error');
    } finally {
      setExporting(false);
    }
  };

  /** Emlékeztető most egy játékos szüleinek (emailben és push értesítésben). */
  const handleRemind = async (playerId: number) => {
    setRemindingId(playerId);
    try {
      const result = await membershipFeesAPI.sendReminders({ playerIds: [playerId], sendEmail: true, sendPush: true });
      const missing = result.playersWithoutRecipient > 0
        ? ` ${result.playersWithoutRecipient} játékosnak nincs szülői fiókja, nekik nem ment.`
        : '';
      showToast(
        result.recipientCount > 0
          ? `Emlékeztető elküldve ${result.recipientCount} címzettnek.${missing}`
          : `Nem ment ki emlékeztető.${missing}`,
        result.recipientCount > 0 ? 'success' : 'error'
      );
      setOutstanding(await membershipFeesAPI.getOutstanding());
    } catch (err) {
      showToast(getErrorMessage(err, 'Az emlékeztetőt nem sikerült elküldeni.'), 'error');
    } finally {
      setRemindingId(null);
    }
  };

  // Staff view (Admin/Coach)
  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
        <div className="flex items-center">
          <BanknotesIcon className="h-7 w-7 text-primary-600 mr-3" />
          <h1 className="text-2xl font-bold text-gray-900">Tagdíjak</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            {exporting ? 'Exportálás...' : `Excel (${year})`}
          </button>
          {isAdmin(user) && (
            <>
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <CogIcon className="h-4 w-4" />
                Beállítások
              </button>
              <button
                onClick={() => setGenerateOpen(true)}
                className="flex items-center gap-1.5 px-3 py-2 bg-primary-600 text-white rounded-md text-sm font-semibold hover:bg-primary-700 disabled:opacity-50"
              >
                <ArrowPathIcon className="h-4 w-4" />
                Tagdíjak generálása
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">{error}</div>
      )}

      {/* Settings Panel */}
      {showSettings && isAdmin(user) && settings && (
        <FeeSettingsPanel
          settings={settings}
          onSettingsUpdated={(s) => { setSettings(s); setShowSettings(false); }}
        />
      )}

      {/* Outstanding Fees Banner */}
      {outstanding && outstanding.totalOutstanding > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div
            className="flex items-center justify-between cursor-pointer"
            onClick={() => setShowOutstandingDetails(!showOutstandingDetails)}
          >
            <div className="flex items-center gap-3">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600 flex-shrink-0" />
              <div>
                <p className="text-lg font-bold text-red-700">
                  Összes kintlévőség: {formatAmount(outstanding.totalOutstanding)}
                </p>
                <p className="text-sm text-red-500">{outstanding.unpaidCount} fizetetlen tagdíj</p>
              </div>
            </div>
            {showOutstandingDetails ? (
              <ChevronUpIcon className="h-5 w-5 text-red-400" />
            ) : (
              <ChevronDownIcon className="h-5 w-5 text-red-400" />
            )}
          </div>
          {showOutstandingDetails && outstanding.byPlayer.length > 0 && isAdmin(user) && (
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-red-700">
                {settings?.reminderEnabled
                  ? `Automatikus emlékeztető: minden hónap ${settings.reminderDayOfMonth}. napján.`
                  : 'Automatikus emlékeztető kikapcsolva (Beállítások).'}
              </p>
              <button
                onClick={() => setRemindOpen(true)}
                disabled={remindingId !== null}
                className="inline-flex items-center justify-center gap-1.5 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                <BellAlertIcon className="h-4 w-4" />
                Emlékeztető küldése...
              </button>
            </div>
          )}
          {showOutstandingDetails && outstanding.byPlayer.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full divide-y divide-red-200">
                <thead>
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-red-600 uppercase">Név</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-red-600 uppercase">Korosztály</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-red-600 uppercase">Hónapok</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-red-600 uppercase">Összeg</th>
                    {isAdmin(user) && (
                      <th className="px-3 py-2 text-right text-xs font-medium text-red-600 uppercase">Emlékeztető</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-red-100">
                  {outstanding.byPlayer.map(p => (
                    <tr key={p.playerId}>
                      <td className="px-3 py-2 text-sm font-medium text-gray-900">{p.playerName}</td>
                      <td className="px-3 py-2 text-sm text-gray-500">{p.ageGroup}</td>
                      <td className="px-3 py-2 text-sm text-center text-gray-500">{p.unpaidMonths}</td>
                      <td className="px-3 py-2 text-sm text-right font-medium text-red-700">{formatAmount(p.outstandingAmount)}</td>
                      {isAdmin(user) && (
                        <td className="px-3 py-2 text-right text-xs whitespace-nowrap">
                          {p.lastReminderSentAt && (
                            <span className="mr-2 text-gray-500">
                              {new Date(p.lastReminderSentAt).toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' })}
                            </span>
                          )}
                          <button
                            onClick={() => handleRemind(p.playerId)}
                            disabled={remindingId !== null}
                            className="rounded border border-red-200 bg-white px-2 py-1 font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                          >
                            {remindingId === p.playerId ? 'Küldés...' : 'Küldés'}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Month Navigation */}
      <div className="flex items-center justify-center gap-4 mb-6">
        <button onClick={goToPrevMonth} className="p-2 hover:bg-gray-100 rounded-full">
          <ChevronLeftIcon className="h-5 w-5" />
        </button>
        <h2 className="text-lg font-semibold text-gray-900 min-w-[180px] text-center">
          {year}. {MONTH_NAMES[month - 1]}
        </h2>
        <button onClick={goToNextMonth} className="p-2 hover:bg-gray-100 rounded-full">
          <ChevronRightIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Statistics */}
      {statistics && <FeeStatisticsPanel statistics={statistics} />}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <input
          type="text"
          placeholder="Keresés név alapján..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500 w-full sm:w-64"
        />
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value as 'all' | 'paid' | 'unpaid')}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500"
        >
          <option value="all">Összes</option>
          <option value="paid">Fizetve</option>
          <option value="unpaid">Nem fizetve</option>
        </select>
        <select
          value={ageGroupFilter}
          onChange={e => setAgeGroupFilter(e.target.value)}
          aria-label="Korosztály"
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500"
        >
          <option value="">Minden korosztály</option>
          {ageGroupOptions.map((g) => (
            <option key={g} value={g}>{g}</option>
          ))}
        </select>
        <span className="self-center text-sm text-gray-600">
          <span className="font-semibold text-gray-900">{filteredFees.length}</span> tagdíj
          {filteredFees.length !== fees.length && <> (összesen {fees.length})</>}
        </span>
      </div>

      {/* Csoportos jelölés - a kijelölés a szűrt listából jön, az aljára tapad */}
      {selectedFeeIds.size > 0 && (
        <div className="sticky bottom-20 z-30 mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-primary-200 bg-white p-3 pr-20 shadow-lg lg:bottom-4">
          <span className="text-sm font-medium text-gray-900">{selectedFeeIds.size} kijelölve</span>
          <button type="button" onClick={() => setSelectedFeeIds(new Set())} className="text-xs text-gray-500 hover:text-gray-700">
            (törlés)
          </button>
          <span className="flex-1" />
          <button
            type="button"
            disabled={bulkPaying}
            onClick={() => handleBulkMark(true)}
            className="rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
          >
            {bulkPaying ? 'Mentés...' : 'Fizetve'}
          </button>
          <button
            type="button"
            disabled={bulkPaying}
            onClick={() => handleBulkMark(false)}
            className="rounded-md border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
          >
            Visszavonás
          </button>
        </div>
      )}

      {/* Mobile Fee Cards - csak az egyik nézet kerül a DOM-ba, adagokban */}
      {isMobile && (
      <div className="grid grid-cols-2 gap-3">
        {filteredFees.length > 0 && (
          <label className="col-span-2 flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={allFilteredSelected} onChange={toggleAllFiltered} className="h-4 w-4 rounded border-gray-300 text-primary-600" />
            {allFilteredSelected ? 'Kijelölés törlése' : `Mind a ${filteredFees.length} kijelölése`}
          </label>
        )}
        {filteredFees.length === 0 ? (
          <div className="col-span-2 text-center py-8 text-sm text-gray-500">
            {fees.length === 0
              ? 'Nincs tagdíj rekord erre a hónapra. Használd a "Generálás" gombot!'
              : 'Nincs találat a szűrésre.'}
          </div>
        ) : (
          visibleFees.map(fee => (
            <div key={fee.id} className={`bg-white rounded-lg shadow border p-3 flex flex-col ${fee.isPaid ? 'border-l-4 border-l-green-400' : 'border-l-4 border-l-red-300'}`}>
              {/* Keskeny kártya: a státusz a név alá kerül, hogy egy hosszú
                  játékosnevet ne nyomjon össze betűnkénti töréssé. */}
              <div className="mb-1.5 space-y-1">
                {/* A jelölőnégyzet és a név együtt kattintható: telefonon a 16 px-es négyzet túl kicsi cél */}
                <label className="-m-1 flex cursor-pointer items-start gap-2 rounded p-1">
                  <input
                    type="checkbox"
                    checked={selectedFeeIds.has(fee.id)}
                    onChange={() => toggleFee(fee.id)}
                    aria-label={`${fee.playerName} kijelölése`}
                    className="mt-0.5 h-5 w-5 shrink-0 rounded border-gray-300 text-primary-600"
                  />
                  <div className="min-w-0">
                  <div className="font-medium text-sm text-gray-900 break-words hyphens-auto leading-snug">{fee.playerName}</div>
                  <div className="text-xs text-gray-500 truncate">{fee.ageGroup}</div>
                  </div>
                </label>
                {fee.isPaid ? (
                  <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-green-700">
                    <CheckCircleIcon className="h-3.5 w-3.5 shrink-0" /> Fizetve
                  </span>
                ) : fee.amount <= 0 ? (
                  <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-gray-500">Mentes (0 Ft)</span>
                ) : (
                  <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-red-700">
                    <XCircleIcon className="h-3.5 w-3.5 shrink-0" /> Nem fizetve
                  </span>
                )}
              </div>
              <div className="flex-1 space-y-0.5">
                <div className="font-bold text-sm text-gray-900">{formatAmount(fee.amount)}</div>
                {fee.paidAt && (
                  <div className="text-xs text-gray-400 truncate">
                    {new Date(fee.paidAt).toLocaleDateString('hu-HU')}
                  </div>
                )}
              </div>
              <div className="mt-2 flex justify-end border-t border-gray-100 pt-1.5">
                <button
                  onClick={() => handleMarkPayment(fee.id, !fee.isPaid)}
                  className={`min-h-[40px] px-4 py-2 rounded-md text-sm font-medium ${
                    fee.isPaid
                      ? 'border border-red-300 text-red-700 bg-white hover:bg-red-50'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {fee.isPaid ? 'Visszavonás' : 'Fizetve'}
                </button>
              </div>
            </div>
          ))
        )}
        {hasMoreFees && <LazyLoadTrigger key={visibleFees.length} onLoadMore={loadMoreFees} className="col-span-2" />}
      </div>
      )}

      {/* Desktop Fee Table */}
      {!isMobile && (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-10 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={toggleAllFiltered}
                    disabled={filteredFees.length === 0}
                    aria-label="A szűrt tagdíjak kijelölése"
                    title={allFilteredSelected ? 'Kijelölés törlése' : `Mind a ${filteredFees.length} kijelölése`}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Név</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Korosztály</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Összeg</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Státusz</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fizetve</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Művelet</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredFees.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                    {fees.length === 0
                      ? 'Nincs tagdíj rekord erre a hónapra. Használd a "Generálás" gombot!'
                      : 'Nincs találat a szűrésre.'}
                  </td>
                </tr>
              ) : (
                visibleFees.map(fee => (
                  <tr key={fee.id} className={fee.isPaid ? 'bg-green-50/30' : 'hover:bg-gray-50'}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedFeeIds.has(fee.id)}
                        onChange={() => toggleFee(fee.id)}
                        aria-label={`${fee.playerName} kijelölése`}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{fee.playerName}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{fee.ageGroup}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                      {editingFeeId === fee.id ? (
                        <div className="flex items-center justify-end gap-1">
                          <input
                            type="number"
                            min="0"
                            value={editAmount}
                            onChange={e => setEditAmount(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleSaveAmount(fee.id); if (e.key === 'Escape') setEditingFeeId(null); }}
                            className="w-24 rounded border border-gray-300 px-2 py-1 text-sm text-right focus:border-primary-500 focus:outline-none"
                            autoFocus
                          />
                          <button onClick={() => handleSaveAmount(fee.id)} className="text-green-600 hover:text-green-800 text-xs font-medium">OK</button>
                          <button onClick={() => setEditingFeeId(null)} className="text-gray-400 hover:text-gray-600 text-xs">X</button>
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          {formatAmount(fee.amount)}
                          {isAdmin(user) && (
                            <button
                              onClick={() => { setEditingFeeId(fee.id); setEditAmount(fee.amount.toString()); }}
                              className="text-gray-300 hover:text-primary-600 ml-1"
                              title="Összeg szerkesztése"
                            >
                              <PencilIcon className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {fee.isPaid ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
                          <CheckCircleIcon className="h-3.5 w-3.5" />
                          Fizetve
                        </span>
                      ) : fee.amount <= 0 ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                          Mentes (0 Ft)
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700">
                          <XCircleIcon className="h-3.5 w-3.5" />
                          Nem fizetve
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {fee.paidAt
                        ? new Date(fee.paidAt).toLocaleDateString('hu-HU')
                        : '-'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleMarkPayment(fee.id, !fee.isPaid)}
                          className={`px-3 py-1 rounded-md text-xs font-medium ${
                            fee.isPaid
                              ? 'border border-red-300 text-red-700 bg-white hover:bg-red-50'
                              : 'bg-green-600 text-white hover:bg-green-700'
                          }`}
                        >
                          {fee.isPaid ? 'Visszavonás' : 'Fizetve'}
                        </button>
                        {isAdmin(user) && (
                          editingOverridePlayerId === fee.playerId ? (
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                min="0"
                                value={overrideAmount}
                                onChange={e => setOverrideAmount(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') handleSaveOverride(fee.playerId); if (e.key === 'Escape') setEditingOverridePlayerId(null); }}
                                placeholder="Üres = alap"
                                className="w-24 rounded border border-gray-300 px-2 py-1 text-xs focus:border-primary-500 focus:outline-none"
                                autoFocus
                              />
                              <button onClick={() => handleSaveOverride(fee.playerId)} className="text-green-600 hover:text-green-800 text-xs font-medium">OK</button>
                              <button onClick={() => setEditingOverridePlayerId(null)} className="text-gray-400 hover:text-gray-600 text-xs">X</button>
                            </div>
                          ) : (
                            <button
                              onClick={() => { setEditingOverridePlayerId(fee.playerId); setOverrideAmount(''); }}
                              className="text-gray-400 hover:text-primary-600"
                              title="Egyedi díj beállítása"
                            >
                              <CogIcon className="h-4 w-4" />
                            </button>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {hasMoreFees && <LazyLoadTrigger key={visibleFees.length} onLoadMore={loadMoreFees} />}
      </div>
      )}

      {isAdmin(user) && (
        <>
          <GenerateFeesModal
            isOpen={generateOpen}
            onClose={() => setGenerateOpen(false)}
            year={year}
            month={month}
            defaultAmount={settings?.defaultMonthlyAmount ?? 0}
            onGenerated={(y, m) => {
              // A generált hónapra ugrunk; ha már azon állt, frissítünk
              if (y === year && m === month) loadStaffData();
              else {
                setYear(y);
                setMonth(m);
              }
              membershipFeesAPI.getOutstanding().then(setOutstanding).catch(() => undefined);
            }}
          />
          <RemindFeesModal
            isOpen={remindOpen}
            onClose={() => setRemindOpen(false)}
            debtors={outstanding?.byPlayer ?? []}
            onSent={() => membershipFeesAPI.getOutstanding().then(setOutstanding).catch(() => undefined)}
          />
        </>
      )}
    </div>
  );
};

export default MembershipFees;
